import React from 'react';
import { useForm } from 'react-hook-form';
import { useRegistration } from '../Registration';
import { useLanguage } from '../../../contexts/LanguageContext';

const Step2PersonalInfo = () => {
  const { formData, updateFormData, nextStep, prevStep } = useRegistration();
  const { t } = useLanguage();
  const copy = t('registration.steps.personal', {});
  const genderOptions = Array.isArray(copy.gender?.options) ? copy.gender.options : [];
  const { register, handleSubmit, formState: { errors } } = useForm({
    defaultValues: {
      firstName: formData.firstName,
      lastName: formData.lastName,
      gender: formData.gender,
      phoneNumber: formData.phoneNumber
    }
  });

  const onSubmit = (data) => {
    updateFormData(data);
    nextStep();
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">
          {copy.title}
        </h2>
        <p className="text-gray-600 text-center mb-8">
          {copy.description}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* First Name */}
        <div>
          <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-2">
            {copy.fields?.firstName?.label}
          </label>
          <input
            {...register('firstName', { 
              required: t('validation.firstNameRequired'),
              minLength: { value: 2, message: t('validation.firstNameMin') }
            })}
            type="text"
            id="firstName"
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-prayer-red focus:border-transparent transition-all duration-200"
            placeholder={copy.fields?.firstName?.placeholder}
          />
          {errors.firstName && (
            <p className="mt-1 text-sm text-red-600">{errors.firstName.message}</p>
          )}
        </div>

        {/* Last Name */}
        <div>
          <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-2">
            {copy.fields?.lastName?.label}
          </label>
          <input
            {...register('lastName', { 
              required: t('validation.lastNameRequired'),
              minLength: { value: 2, message: t('validation.lastNameMin') }
            })}
            type="text"
            id="lastName"
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-prayer-red focus:border-transparent transition-all duration-200"
            placeholder={copy.fields?.lastName?.placeholder}
          />
          {errors.lastName && (
            <p className="mt-1 text-sm text-red-600">{errors.lastName.message}</p>
          )}
        </div>
      </div>

      {/* Gender */}
      <div>
        <label htmlFor="gender" className="block text-sm font-medium text-gray-700 mb-2">
          {copy.gender?.label}
        </label>
        <select
          {...register('gender', { required: t('validation.genderRequired') })}
          id="gender"
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
        >
          <option value="">{copy.gender?.placeholder}</option>
          {genderOptions.map((option) => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </select>
        {errors.gender && (
          <p className="mt-1 text-sm text-red-600">{errors.gender.message}</p>
        )}
      </div>

      {/* Phone Number */}
      <div>
        <label htmlFor="phoneNumber" className="block text-sm font-medium text-gray-700 mb-2">
          {copy.fields?.phoneNumber?.label}
        </label>
        <input
          {...register('phoneNumber', { 
            required: t('validation.phoneRequired'),
            pattern: {
              value: /^[0-9+\-()\s]+$/,
              message: t('validation.phoneInvalid')
            }
          })}
          type="tel"
          id="phoneNumber"
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-prayer-red focus:border-transparent transition-all duration-200"
          placeholder={copy.fields?.phoneNumber?.placeholder}
        />
        {errors.phoneNumber && (
          <p className="mt-1 text-sm text-red-600">{errors.phoneNumber.message}</p>
        )}
        <p className="mt-2 text-sm text-gray-500">
          {copy.fields?.phoneNumber?.helper}
        </p>
      </div>

      {/* Navigation Buttons */}
      <div className="flex justify-between pt-6">
        <button
          type="button"
          onClick={prevStep}
          className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300 transition-all duration-200"
        >
          {t('registration.previous')}
        </button>
        
        <button
          type="submit"
          className="px-6 py-3 bg-prayer-red text-white rounded-lg font-medium hover:bg-prayer-red-light transition-all duration-200"
        >
          {t('registration.next')}
        </button>
      </div>
    </form>
  );
};

export default Step2PersonalInfo;
